import { useQuery } from '@tanstack/react-query';
import {
  Beacon,
  Device,
  Plane,
  DeviceEntryProps,
  DeviceType,
  ECoordsSrid,
  Gateway,
  PlaneEntryProps,
  SiteEntryProps,
} from '@ixicare/resources';
import _ from 'lodash';
import { useSearchParams } from 'react-router-dom';
import React, { RefObject } from 'react';
import {
  GoToMarkerEvent,
  IndoorMapMarker,
  OnMarkerInsertEvent,
  OnMarkerPressedEvent,
  OnPlaneSelectedEvent,
} from '~components/Map/DynamicMap';
import { MapEvent } from '~config/map';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import { mapDBGWithType } from '~modules/devices/utils/deviceMapping';
import { MapPostEventWrapperRef } from '~components/Map/MapPostEventWrapper';
import { useDeviceDetail } from './useDeviceDetail';

interface UseSitePlaneMapProps {
  site: SiteEntryProps;
  initialPlaneId: number;
  mapRef: RefObject<MapPostEventWrapperRef>;
}

const transformDeviceToMarker = (b: DBGWithType): IndoorMapMarker => {
  const type = b.type.toLowerCase();
  const coords = b.installationCoords?.transformations?.find((t) => t.srid === ECoordsSrid.Indoor);
  return {
    id: `${type}::${b.id}`,
    long: coords?.x ?? 0,
    lat: coords?.y ?? 0,
    label: b.description ?? b.macAddress,
    batteryLevel: (b as DeviceEntryProps).batteryLevel,
    type,
    subType: b.subType ? b.description + '  - ' + b.subType : b.description,
  };
};

export const useSitePlaneMap = ({ site, initialPlaneId, mapRef }: UseSitePlaneMapProps) => {
  const [searchParams] = useSearchParams();

  const [planeId, setPlaneId] = React.useState(initialPlaneId);
  const [planeLoaded, setPlaneLoaded] = React.useState(false);

  const initialPlane = site.planes.find((p: PlaneEntryProps) => p.id === planeId);

  const [planeCdn, setPlaneCdn] = React.useState(initialPlane?.cdnUrl);
  const [isPlaneImageProcessed, setPlaneImageProcessed] = React.useState(initialPlane?.isPlaneImageProcessed);

  const { data: polledPlane, refetch: refetchPolledPlane } = useQuery<PlaneEntryProps>({
    queryKey: ['polled-plane', planeId],
    queryFn: async () => {
      return Plane.getPlane(planeId);
    },
    enabled: !!initialPlane && !planeLoaded && !!planeCdn && !isPlaneImageProcessed,
    refetchInterval: 5000,
  });

  const plane = React.useMemo(() => {
    if (polledPlane && polledPlane.id === planeId) {
      setPlaneCdn(polledPlane.cdnUrl);
      setPlaneImageProcessed(polledPlane.isPlaneImageProcessed);
      return polledPlane;
    }
    return initialPlane;
  }, [initialPlane, polledPlane, planeId]);

  const initialCheckBoxState = {
    showLegend: false,
    showRooms: false,
    showRoomLabels: false,
    showBeacons: true,
    showGateways: true,
    showButtons: true,
  };

  const checkBoxStateReducer = (state, action) => {
    state[action.target.name] = action.target.checked;
    return { ...state };
  };

  const [checkboxState, dispatch] = React.useReducer(checkBoxStateReducer, initialCheckBoxState);

  const {
    data: beacons,
    isLoading: isLoadingBeacons,
    refetch: refetchBeacons,
  } = useQuery({
    queryKey: ['beacons-for-plane', planeId],
    queryFn: () => Beacon.getBeacons({ planeId }),
    enabled: checkboxState.showBeacons,
  });
  const {
    data: gateways,
    isLoading: isLoadingGateways,
    refetch: refetchGateways,
  } = useQuery({
    queryKey: ['gateways-for-plane', planeId],
    queryFn: () => Gateway.getGateways({ planeId }),
    enabled: checkboxState.showGateways,
  });
  const {
    data: buttons,
    isLoading: isLoadingButtons,
    refetch: refetchButtons,
  } = useQuery({
    queryKey: ['buttons-for-plane', planeId],
    queryFn: () =>
      Device.getDevices({
        planeId,
        deviceType: DeviceType.WALL_MOUNTED_DISTRESS_BUTTON,
      }),
    enabled: checkboxState.showButtons,
  });

  const allQueriesLoaded =
    (!checkboxState.showBeacons || !isLoadingBeacons) &&
    (!checkboxState.showGateways || !isLoadingGateways) &&
    (!checkboxState.showButtons || !isLoadingButtons);

  const planeDbgs = React.useMemo(() => {
    let items: DBGWithType[] = [];
    if (checkboxState.showBeacons && beacons) {
      items = items.concat(mapDBGWithType(beacons, DeviceType.BEACON));
    }
    if (checkboxState.showGateways && gateways) {
      items = items.concat(mapDBGWithType(gateways, DeviceType.GATEWAY));
    }
    if (checkboxState.showButtons && buttons) {
      items = items.concat(mapDBGWithType(buttons, DeviceType.WALL_MOUNTED_DISTRESS_BUTTON));
    }
    return items;
  }, [checkboxState, beacons, gateways, buttons]);

  const markers = React.useMemo(() => planeDbgs.map(transformDeviceToMarker), [planeDbgs]);

  const {
    selectedDevices,
    setSelectedDevices,
    selectedDeviceDetail,
    isEditingDevice,
    setIsEditingDevice,
    handleCloseDetail: baseHandleCloseDetail,
  } = useDeviceDetail(planeDbgs);

  const [goToDevice, setGoToDevice] = React.useState<GoToMarkerEvent | undefined>(undefined);

  const [isMoving, setIsMoving] = React.useState(false);
  const [newLocation, setNewLocation] = React.useState<OnMarkerInsertEvent>();

  const onMarkerPressed = React.useCallback(
    (e: OnMarkerPressedEvent) => {
      if (!e || !e.id) return;
      const [type, id] = e.id.split('::');
      if (!isMoving) {
        const device = planeDbgs.find((x) => x.type === (type.toUpperCase() as DeviceType) && x.id.toString() === id);
        if (device) setSelectedDevices([device]);
      }
    },
    [isMoving, planeDbgs]
  );

  const handleRefresh = () => {
    refetchBeacons();
    refetchButtons();
    refetchGateways();
    refetchPolledPlane();
    setPlaneLoaded(false);
  };

  const onPlaneSelectedEvent = React.useCallback((e: OnPlaneSelectedEvent) => {
    const floor = site.planes.find((fl) => fl.id === e.id);
    if (floor) {
      setPlaneId(floor.id);
      handleRefresh();
      setPlaneLoaded(true);
    }
  }, []);

  // When navigating to a device via goToDevice, select it
  React.useEffect(() => {
    if (!selectedDevices && goToDevice) {
      const [type, id] = goToDevice.id.split('::').map((x) => {
        return _.isString(x) ? x.toUpperCase() : x;
      });
      const device = planeDbgs.find((x) => x.type === type && x.id.toString() === id);
      if (device) {
        setSelectedDevices([device]);
      }
    }
  }, [goToDevice, planeDbgs]);

  const handleCloseDetail = React.useCallback(() => {
    baseHandleCloseDetail();
    setNewLocation(undefined);
    setGoToDevice(undefined);
    setIsMoving(false);
    mapRef.current?.sendEvent(MapEvent.DeviceMapOverlayClosed);
  }, [baseHandleCloseDetail]);

  React.useEffect(() => {
    setNewLocation(undefined);
    setIsMoving(false);
  }, [selectedDevices]);

  React.useEffect(() => {
    if (!isMoving && newLocation) {
      setSelectedDevices(undefined);
    }
  }, [newLocation]);

  React.useEffect(() => {
    setNewLocation(undefined);
  }, [planeId]);

  const markerFromQueryParam = searchParams.get('marker');
  React.useEffect(() => {
    if (allQueriesLoaded && markerFromQueryParam) {
      const goToMarker = markers.find((x) => x.id === markerFromQueryParam);

      if (goToMarker) {
        setGoToDevice({
          id: goToMarker.id,
          lat: goToMarker.lat,
          long: goToMarker.long,
        });
      }
    }
  }, [markerFromQueryParam, markers, allQueriesLoaded]);

  React.useEffect(() => {
    if (plane == undefined) {
      return window.location.reload();
    }

    if (!!plane.cdnUrl && !plane.isPlaneImageProcessed) {
      return setPlaneLoaded(false);
    }

    return setPlaneLoaded(true);
  }, [plane]);

  return {
    plane,
    planeLoaded,
    state: checkboxState,
    dispatch,
    markers,
    planeDbgs,
    selectedDevices,
    selectedDeviceDetail,
    goToDevice,
    setGoToDevice,
    isMoving,
    setIsMoving,
    newLocation,
    setNewLocation,
    isEditingDevice,
    setIsEditingDevice,
    onMarkerPressed,
    onPlaneSelectedEvent,
    handleCloseDetail,
    handleRefresh,
  };
};
